import {
  CaseTemplateFormValues,
  FileCategoryFormValue,
} from "src/cases/templates/caseTemplateFormUtils";

// Reserved keys that cannot be used for any user-defined properties
export const RESERVED_SYSTEM_KEYS = [
  "queue_id",
  "assignee_id",
  "updated_at",
  "created_at",
  "updated_by",
  "env",
  "id",
] as const;

export const isReservedSystemKey = (key: string): boolean => {
  return RESERVED_SYSTEM_KEYS.includes(
    key as (typeof RESERVED_SYSTEM_KEYS)[number],
  );
};

const validateReservedSystemKey = (key: string): string | boolean => {
  if (isReservedSystemKey(key)) {
    return `"${key}" is a reserved system field and cannot be used`;
  }
  return true;
};

const validatePropertyUniqueKey = (
  value: string,
  formValues: CaseTemplateFormValues,
): string | boolean => {
  // Check for duplicates within case properties
  const casePropertyKeys = formValues.attributes.case_properties
    .map((prop) => prop.key)
    .filter((key) => key === value);

  if (casePropertyKeys.length > 1) {
    return `Property name "${value}" is already used by another case property`;
  }

  return true;
};

export const validateCasePropertyCrossComponentUniqueKey = (
  value: string,
  formValues: CaseTemplateFormValues,
): string | boolean => {
  if (!value) return true; // Let required validator handle empty values

  // Check for conflicts with additional input keys
  const additionalInputKeys = formValues.attributes.additional_inputs || [];
  const conflictingInput = additionalInputKeys.find(
    (input) => input.key === value,
  );

  if (conflictingInput) {
    return `Property name "${value}" is already used by an additional input field "${conflictingInput.display_name}"`;
  }

  return true;
};

const validateAdditionalInputUniqueKey = (
  value: string,
  formValues: CaseTemplateFormValues,
): string | boolean => {
  const allKeys = formValues.attributes.additional_inputs;
  const duplicates = allKeys.filter((input) => input.key === value);

  if (duplicates.length > 1) {
    return `Display name must transform into a unique key: ${duplicates
      .map((input) => `"${input.display_name}"`)
      .join(", ")} transform into the same key: ${value}`;
  }

  return true;
};

export const validateAdditionalInputCrossComponentUniqueKey = (
  value: string,
  formValues: CaseTemplateFormValues,
): string | boolean => {
  if (!value) return "Key is required";

  // Check for conflicts with case properties
  const casePropertyKeys = formValues.attributes.case_properties || [];
  const conflictingProperty = casePropertyKeys.find(
    (prop) => prop.key === value,
  );

  if (conflictingProperty) {
    return `Key "${value}" is already used by a case property "${conflictingProperty.display_name}"`;
  }

  return true;
};

/**
 * Validation object for use with React Hook Form's validate option
 * Example usage: validate: casePropertyKeyValidators
 */
export const casePropertyKeyValidators = {
  propertyUniqueKey: validatePropertyUniqueKey,
  reservedSystemKey: validateReservedSystemKey,
  crossComponentUnique: validateCasePropertyCrossComponentUniqueKey,
};

const validateFileCategoryUniqueKey = (
  value: string,
  formValues: CaseTemplateFormValues,
): string | boolean => {
  const allKeys = formValues.attributes.file_categories || [];
  const duplicates = allKeys.filter(
    (category: FileCategoryFormValue) => category.key === value,
  );

  if (duplicates.length > 1) {
    return `Category key "${value}" is already used by another file category`;
  }

  return true;
};

const validateFileCategoryKeyFormat = (value: string): string | boolean => {
  if (!value) return true;

  if (!isValidKey(value)) {
    return "Category key must contain only lowercase letters, numbers, and underscores.";
  }

  return true;
};

/**
 * Validation object for use with React Hook Form's validate option
 * Example usage: validate: additionalInputKeyValidators
 */
export const additionalInputKeyValidators = {
  additionalInputUniqueKey: validateAdditionalInputUniqueKey,
  reservedSystemKey: validateReservedSystemKey,
  crossComponentUnique: validateAdditionalInputCrossComponentUniqueKey,
};

/**
 * Validation object for use with React Hook Form's validate option
 * Example usage: validate: fileCategoryKeyValidators
 */
export const fileCategoryKeyValidators = {
  fileCategoryUniqueKey: validateFileCategoryUniqueKey,
  fileCategoryKeyFormat: validateFileCategoryKeyFormat,
};

/**
 * Helper function to check if a key is valid (lowercase letters, numbers, underscores, starts with a letter)
 */
const isValidKey = (key: string) => {
  const validKeyPattern = /^[a-z][a-z0-9_]*$/;
  return validKeyPattern.test(key);
};

/**
 * Generic validation function to ensure item keys are unique
 * Works for both tasks and actions
 */
const createUniqueItemKeyValidator =
  (itemType: "task" | "action") =>
  (value: string, formValues: CaseTemplateFormValues) => {
    if (!value) return true;

    const basePath = itemType === "task" ? "case_tasks" : "case_actions";
    const items = formValues?.attributes?.[basePath] ?? [];
    const duplicateKeys = items.filter((item) => item.key === value);

    if (duplicateKeys.length > 1) {
      const itemLabel = itemType === "task" ? "Task" : "Action";
      return `${itemLabel} key "${value}" is already used by another ${itemType}`;
    }

    return true;
  };

/**
 * Generic validation function to ensure item keys follow the required format
 */
const validateItemKeyFormat =
  (itemType: "task" | "action") => (value: string | undefined) => {
    if (!value) return true;

    if (!isValidKey(value)) {
      const itemLabel = itemType === "task" ? "Task" : "Action";
      return `${itemLabel} key must contain only lowercase letters, numbers, and underscores.`;
    }

    return true;
  };

/**
 * Generic validator to check if a field is empty
 */
const createEmptyFieldValidator =
  (field: string) => (value: string | undefined) => {
    return !!value?.trim().length || `${field} cannot be empty`;
  };

/**
 * Generic validator to check if a field exceeds max length
 */
const createMaxLengthValidator =
  (field: string, maxLength: number) => (value: string | undefined) => {
    if (!value) return true;
    return (
      value.length <= maxLength ||
      `${field} must not be more than ${maxLength} characters`
    );
  };

/**
 * Generic validation object factory for item keys
 * Works for both tasks and actions
 */
export const createItemKeyValidators = (itemType: "task" | "action") => {
  const itemLabel = itemType === "task" ? "Task" : "Action";
  return {
    validateEmptyField: createEmptyFieldValidator(`${itemLabel} key`),
    validateUniqueItemKey: createUniqueItemKeyValidator(itemType),
    validateItemKeyFormat: validateItemKeyFormat(itemType),
  };
};

/**
 * Generic validation object factory for item names
 * Works for both tasks and actions
 */
export const createItemNameValidators = (itemType: "task" | "action") => {
  const itemLabel = itemType === "task" ? "Task" : "Action";
  return {
    validateEmptyField: createEmptyFieldValidator(`${itemLabel} name`),
  };
};

/**
 * Generic validation function to ensure form field keys are unique
 * Works for both tasks and actions
 */
const createUniqueFormFieldKeyValidator =
  (itemType: "task" | "action", itemIndex: number) =>
  (value: string | undefined, formValues: CaseTemplateFormValues) => {
    if (!value) return true;

    const basePath = itemType === "task" ? "case_tasks" : "case_actions";
    const fields =
      formValues?.attributes?.[basePath]?.[itemIndex].form?.fields ?? [];
    const duplicateKeys = fields.filter((field) => field.key === value);

    if (duplicateKeys.length > 1) {
      return `Field key "${value}" is already used`;
    }

    return true;
  };

/**
 * Generic validation object for form field names
 * Works for both tasks and actions
 */
export const createFormFieldNameValidators = () => ({
  validateEmptyField: createEmptyFieldValidator("Field name"),
});

/**
 * Generic validation object factory for form field keys
 * Works for both tasks and actions
 */
export const createFormFieldKeyValidators = (
  itemType: "task" | "action",
  itemIndex: number,
) => ({
  validateEmptyField: createEmptyFieldValidator("Field key"),
  validateFormFieldKeyFormat: validateEditorFormFieldKeyFormat,
  validateUniqueFormFieldKey: createUniqueFormFieldKeyValidator(
    itemType,
    itemIndex,
  ),
});

/**
 * Generic validation function to ensure form field options are unique
 * Works for both tasks and actions
 */
const createFormFieldOptionsValidator =
  (itemType: "task" | "action", itemIndex: number, fieldIndex: number) =>
  (value: string | undefined, formValues: CaseTemplateFormValues) => {
    if (!value) return true;

    const basePath = itemType === "task" ? "case_tasks" : "case_actions";
    const field =
      formValues?.attributes?.[basePath]?.[itemIndex].form?.fields?.[
        fieldIndex
      ];

    if (field?.type !== "select" && field?.type !== "multi_select") {
      // [IMPOSSIBLE] This should never happen as options are only rendered for select and multi_select fields
      // We handle it gracefully
      return true;
    }

    const options = field.options.filter((opt) => opt === value);

    if (options.length > 1) {
      return `Option "${value}" is already used`;
    }

    return true;
  };

/**
 * Generic validation object factory for form field options
 * Works for both tasks and actions
 * Example usage: validate: createFormFieldOptionValidators("task", taskIndex, fieldIndex)
 */
export const createFormFieldOptionValidators = (
  itemType: "task" | "action",
  itemIndex: number,
  fieldIndex: number,
) => ({
  validateEmptyField: createEmptyFieldValidator("Option"),
  validateMaxLength: createMaxLengthValidator("Option", 100),
  validateFormFieldOptions: createFormFieldOptionsValidator(
    itemType,
    itemIndex,
    fieldIndex,
  ),
});

/**
 * Validation object for use with React Hook Form's validate option
 * Example usage: validate: caseActionDecisionFlowValidators
 */
export const caseActionDecisionFlowValidators = {
  validateDecisionFlow: (
    value: MaybeEmpty<{
      flow_id?: string;
      flow_version_id?: string;
    }>,
  ) => {
    if (!value || !value?.flow_id || !value?.flow_version_id) {
      return "A decision flow and version must be selected";
    }

    return true;
  },
};

type MaybeEmpty<T> = T | null | undefined;

/**
 * Validation function to ensure form field keys follow the required format
 */
const validateEditorFormFieldKeyFormat = (value: string | undefined) => {
  if (!value) return true;

  if (!isValidKey(value)) {
    return "Field key must contain only lowercase letters, numbers, and underscores.";
  }

  return true;
};
